import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Stack,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  ListItemButton,
  Grid,
  Pagination,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
  Publish as PublishIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MainLayout from '../components/MainLayout';
import { useAppSelector } from '../hooks/redux';
import { apiService, getObjectionsGrouped, getFAQsGrouped, getLandmines, getCompetitors, searchDictionary, getSalesPlaybookIndustries } from '../services/apiService';
import { TopicCategory, TOPIC_CATEGORY_LABELS, COMPETITOR_TYPE_LABELS } from '../constants/topicCategories';
import { DifficultyLevel, PushbackLevel, DIFFICULTY_LABELS, PUSHBACK_LABELS } from '../constants/trainingConstants';

interface TrackItem {
  itemId: string;
  type: 'objection' | 'faq' | 'landmine' | 'competitor';
  contentId: string;
  order: number;
  contentSnapshot: any;
}

interface PlaybookConfigFormData {
  enabled: boolean;
  industry: string;
  roleName: string;
}

interface TrackFormData {
  trackName: string;
  description: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  pushbackLevel: 'low' | 'medium' | 'high';
  customerTone: 'neutral' | 'frustrated' | 'curious' | 'aggressive' | 'skeptical';
  estimatedDuration: number;
  tags: string[];
  items: TrackItem[];
  assignedUsers: string[];
  playbookConfig: PlaybookConfigFormData;
}

interface SortableItemProps {
  item: TrackItem;
  onDelete: (itemId: string) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ item, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.itemId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getItemTypeColor = (type: string) => {
    switch (type) {
      case 'objection':
        return 'primary';
      case 'faq':
        return 'success';
      case 'landmine':
        return 'error';
      case 'competitor':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Card ref={setNodeRef} style={style} sx={{ mb: 1 }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
        <IconButton {...attributes} {...listeners} size="small">
          <DragIcon />
        </IconButton>

        <Box flex={1}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <Chip
              label={item.type}
              color={getItemTypeColor(item.type)}
              size="small"
            />
          </Stack>
          <Typography variant="body2">
            {item.contentSnapshot?.questions?.[0] ||
             item.contentSnapshot?.question ||
             item.contentSnapshot?.triggers?.[0] ||
             item.contentSnapshot?.trigger ||
             item.contentSnapshot?.name ||
             '⚠️ Unknown item - missing content data'}
          </Typography>
          {item.contentSnapshot?.questions && item.contentSnapshot.questions.length > 1 && (
            <Typography variant="caption" color="primary" sx={{ mt: 0.5 }}>
              +{item.contentSnapshot.questions.length - 1} variations
            </Typography>
          )}
          {item.contentSnapshot?.triggers && item.contentSnapshot.triggers.length > 1 && (
            <Typography variant="caption" color="primary" sx={{ mt: 0.5 }}>
              +{item.contentSnapshot.triggers.length - 1} trigger variations
            </Typography>
          )}
          {(!item.contentSnapshot?.questions && !item.contentSnapshot?.question && !item.contentSnapshot?.triggers && !item.contentSnapshot?.trigger && !item.contentSnapshot?.name) && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              ContentSnapshot: {JSON.stringify(item.contentSnapshot)}
            </Typography>
          )}
        </Box>

        <IconButton size="small" color="error" onClick={() => onDelete(item.itemId)}>
          <DeleteIcon />
        </IconButton>
      </CardContent>
    </Card>
  );
};

const TrainingTrackBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { trackId } = useParams();
  const isEditMode = Boolean(trackId);
  const { user } = useAppSelector((state) => state.auth);
  const currentUserUid = user?.uid ?? '';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState<TrackFormData>({
    trackName: '',
    description: '',
    difficulty: 'intermediate',
    pushbackLevel: 'medium',
    customerTone: 'neutral',
    estimatedDuration: 10,
    tags: [],
    items: [],
    assignedUsers: user?.uid ? [user.uid] : [],
    playbookConfig: {
      enabled: false,
      industry: '',
      roleName: '',
    },
  });

  const [tagInput, setTagInput] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [simulationTab, setSimulationTab] = useState(0);
  const [playbookIndustries, setPlaybookIndustries] = useState<Array<{ industry: string; displayName: string; roles: Array<{ name: string; displayName: string }> }>>([]);

  // Item selection dialog
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [itemType, setItemType] = useState<'objection' | 'faq' | 'landmine' | 'competitor'>('objection');
  const [availableItems, setAvailableItems] = useState<any[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [currentItemPage, setCurrentItemPage] = useState(1);
  const [totalItemPages, setTotalItemPages] = useState(1);
  const [loadingItems, setLoadingItems] = useState(false);
  const [addingItems, setAddingItems] = useState(false);
  const itemsPerPage = 25;

  // Search state for item dialog
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [isItemSearchMode, setIsItemSearchMode] = useState(false);
  const [itemSearchResults, setItemSearchResults] = useState<any[]>([]);
  const [itemSearchLoading, setItemSearchLoading] = useState(false);

  // User assignment
  const [users, setUsers] = useState<any[]>([]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load track data if editing
  useEffect(() => {
    if (isEditMode && trackId) {
      loadTrackData(trackId);
    }
  }, [isEditMode, trackId]);

  // Load users for assignment + playbook industries
  useEffect(() => {
    loadUsers();
    loadPlaybookIndustries();
  }, []);

  const loadTrackData = async (id: string) => {
    try {
      setLoading(true);
      const response = await apiService.get(`/training/tracks/${id}`);
      if (response.success && response.data) {
        const track = response.data;
        setFormData({
          trackName: track.trackName,
          description: track.description,
          difficulty: track.difficulty,
          pushbackLevel: track.pushbackLevel,
          customerTone: track.customerTone || 'neutral',
          estimatedDuration: track.estimatedDuration,
          tags: track.tags || [],
          items: track.items || [],
          assignedUsers: currentUserUid
            ? [...new Set([currentUserUid, ...(track.assignedUsers || [])])]
            : track.assignedUsers || [],
          playbookConfig: {
            enabled: track.playbookConfig?.enabled ?? false,
            industry: track.playbookConfig?.industry ?? '',
            roleName: track.playbookConfig?.roleName ?? '',
          },
        });
      }
    } catch (error: any) {
      setError(error.error || error.message || 'Failed to load track');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await apiService.get('/admin/users');
      if (response.success && response.data) {
        setUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadPlaybookIndustries = async () => {
    try {
      const industries = await getSalesPlaybookIndustries();
      setPlaybookIndustries(industries);
    } catch (error) {
      console.error('Error loading playbook industries:', error);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setFormData((prev) => {
        const items = [...prev.items];
        const oldIndex = items.findIndex((item) => item.itemId === active.id);
        const newIndex = items.findIndex((item) => item.itemId === over.id);

        const reorderedItems = arrayMove(items, oldIndex, newIndex).map((item, index) => ({
          ...item,
          order: index,
        }));

        return { ...prev, items: reorderedItems };
      });
    }
  };

  const loadAvailableItems = async (type: 'objection' | 'faq' | 'landmine' | 'competitor', page: number = 1) => {
    setLoadingItems(true);
    try {
      let items: any[] = [];
      let totalPages = 1;

      switch (type) {
        case 'objection':
          const objResponse = await getObjectionsGrouped(page, itemsPerPage);
          // Only show original objections (not variations)
          items = objResponse.objections.filter(item => item.isOriginal === true);
          totalPages = objResponse.pagination?.totalPages || 1;
          break;
        case 'faq':
          const faqResponse = await getFAQsGrouped(page, itemsPerPage);
          // Only show original FAQs (not variations)
          items = faqResponse.faqs.filter(item => item.isOriginal === true);
          totalPages = faqResponse.pagination?.totalPages || 1;
          break;
        case 'landmine':
          // Only show compliance landmines (legal_risk), not detractors
          // Detractors are things the salesperson should avoid saying — not something a customer would bring up
          const landmineResponse = await getLandmines(page, itemsPerPage, { landmineType: 'legal_risk' });
          items = landmineResponse.landmines;
          totalPages = landmineResponse.pagination?.totalPages || 1;
          break;
        case 'competitor':
          const compResponse = await getCompetitors(page, itemsPerPage);
          items = compResponse.competitors;
          totalPages = compResponse.pagination?.totalPages || 1;
          break;
      }

      setAvailableItems(items);
      setTotalItemPages(totalPages);
      setCurrentItemPage(page);
    } catch (error) {
      console.error('Error loading items:', error);
      setError('Failed to load items');
    } finally {
      setLoadingItems(false);
    }
  };

  const handleItemSearch = async (query: string, page: number = 1) => {
    // Only perform search for objections and FAQs
    if (itemType !== 'objection' && itemType !== 'faq') {
      return;
    }

    if (!query.trim()) {
      // Empty search - exit search mode
      setIsItemSearchMode(false);
      setItemSearchResults([]);
      setItemSearchTerm('');
      return;
    }

    // Don't search if query is too short
    if (query.trim().length < 2) {
      return;
    }

    setItemSearchLoading(true);

    try {
      const type = itemType === 'objection' ? 'objection' : 'faq';
      const response = await searchDictionary(query, type, page, itemsPerPage);

      if (response.success) {
        // Filter to only show originals for consistency with regular loading
        const originals = response.results.filter(item => item.isOriginal === true);
        console.log('Search results:', originals.map(o => ({ id: o._id, question: o.question })));
        console.log('Current selectedItems:', selectedItems);
        setItemSearchResults(originals);
        setTotalItemPages(response.pagination.totalPages || 1);
        setCurrentItemPage(page);
        setIsItemSearchMode(true);
      } else {
        // Don't set global error for search issues in modal
        console.error('Search failed:', response.error);
        setIsItemSearchMode(false);
      }
    } catch (err: any) {
      // Don't set global error for search issues in modal
      console.error('Failed to search:', err);
      setIsItemSearchMode(false);
    } finally {
      setItemSearchLoading(false);
    }
  };

  // Debounced search effect for item dialog
  useEffect(() => {
    // Only set up debouncing for objections and FAQs
    if (!itemDialogOpen || (itemType !== 'objection' && itemType !== 'faq')) {
      return;
    }

    const debounceTimer = setTimeout(() => {
      if (itemSearchTerm.trim()) {
        handleItemSearch(itemSearchTerm, 1);
      } else {
        setIsItemSearchMode(false);
        setItemSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [itemSearchTerm, itemType, itemDialogOpen]);

  const handleOpenItemDialog = (type: 'objection' | 'faq' | 'landmine' | 'competitor') => {
    setItemType(type);
    setItemDialogOpen(true);

    // Pre-select items that are already in the track for this type
    const existingContentIds = formData.items
      .filter((item) => item.type === type)
      .map((item) => item.contentId);

    setSelectedItems(existingContentIds);

    // Reset search state
    setItemSearchTerm('');
    setIsItemSearchMode(false);
    setItemSearchResults([]);

    setCurrentItemPage(1);
    loadAvailableItems(type, 1);
  };

  const handleItemPageChange = (newPage: number) => {
    if (isItemSearchMode && itemSearchTerm.trim()) {
      handleItemSearch(itemSearchTerm, newPage);
    } else {
      loadAvailableItems(itemType, newPage);
    }
  };

  const handleAddItems = async () => {
    setAddingItems(true);
    try {
      // Get currently existing items of this type in the track
      const existingItemsOfType = formData.items.filter((item) => item.type === itemType);
      const existingContentIds = existingItemsOfType.map((item) => item.contentId);

      // Find items to add (newly selected)
      const itemsToAdd = selectedItems.filter((id) => !existingContentIds.includes(id));

      // Keep all existing items in their original positions, but remove unchecked ones of this type
      let updatedItems = formData.items.filter((item) =>
        item.type !== itemType || selectedItems.includes(item.contentId)
      );

      // Create new items for newly selected content
      // Fetch minimal display info only (not full snapshot - backend will fetch fresh content at session start)
      const newItems = await Promise.all(
        itemsToAdd.map(async (contentId, index) => {
          let content = availableItems.find((item) => item._id === contentId);

          // If content not in current page, fetch it for display purposes only
          if (!content) {
            try {
              let response: any;
              switch (itemType) {
                case 'objection':
                  response = await apiService.get(`/dictionary/objections/${contentId}`);
                  content = response.success ? response.data : response;
                  break;
                case 'faq':
                  response = await apiService.get(`/dictionary/faqs/${contentId}`);
                  content = response.success ? response.data : response;
                  break;
                case 'landmine':
                  response = await apiService.get(`/landmines/${contentId}`);
                  // Handle landmine response structure (uses 'landmine' field)
                  content = response.success ? (response.landmine || response.data) : response;
                  break;
                case 'competitor':
                  response = await apiService.get(`/competitors/${contentId}`);
                  content = response.success ? response.data : response;
                  break;
              }
            } catch (error) {
              console.error('❌ Failed to fetch content for contentId:', contentId, error);
              return null;
            }
          }

          // Validate content exists
          if (!content) {
            console.error('❌ Content not found for contentId:', contentId);
            return null;
          }

          // Build minimal display snapshot (only for UI display, not saved to backend)
          // Backend will fetch fresh content at session start
          let contentSnapshot: any = {};

          switch (itemType) {
            case 'objection':
            case 'faq':
              // Only store basic display info (first question)
              contentSnapshot = {
                question: content.question || '',
                questions: content.variations && content.variations.length > 0
                  ? [content.question, ...content.variations.map((v: any) => v.question).filter(Boolean)]
                  : [content.question],
              };
              break;
            case 'landmine':
              // Only store basic display info (first trigger)
              contentSnapshot = {
                trigger: content.trigger || '',
                triggers: content.triggerVariations && content.triggerVariations.length > 0
                  ? [content.trigger, ...content.triggerVariations]
                  : [content.trigger],
              };
              break;
            case 'competitor':
              // Only store name for display
              contentSnapshot = {
                name: content.name || '',
              };
              break;
          }

          return {
            itemId: `${Date.now()}-${index}`,
            type: itemType,
            contentId,
            order: updatedItems.length + index,
            contentSnapshot, // Minimal snapshot for UI display only
          } as TrackItem;
        })
      );

      // Filter out null items and add to updated list
      const validNewItems = newItems.filter((item): item is TrackItem => item !== null);
      updatedItems = [...updatedItems, ...validNewItems];

      // Reorder all items
      updatedItems = updatedItems.map((item, index) => ({
        ...item,
        order: index,
      }));

      setFormData((prev) => ({
        ...prev,
        items: updatedItems,
      }));

      setItemDialogOpen(false);
      setSelectedItems([]);
    } catch (error) {
      console.error('Error adding items:', error);
      setError('Failed to add items. Please try again.');
    } finally {
      setAddingItems(false);
    }
  };

  const handleDeleteItem = (itemId: string) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.itemId !== itemId).map((item, index) => ({
        ...item,
        order: index,
      })),
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleDeleteTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  };

  const handleSave = async (publish: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      if (!formData.trackName.trim()) {
        setError('Track name is required');
        return;
      }

      if (formData.items.length === 0 && !formData.playbookConfig.enabled) {
        setError('Please add knowledge base items or enable playbook practice');
        return;
      }

      // Strip contentSnapshot from items before sending to backend
      // Backend will fetch fresh content at session start
      const itemsWithoutSnapshot = formData.items.map(({ contentSnapshot, ...item }) => item);

      const payload = {
        ...formData,
        items: itemsWithoutSnapshot,
        status: publish ? 'active' : 'draft',
      };

      let response;
      if (isEditMode && trackId) {
        response = await apiService.put(`/training/tracks/${trackId}`, payload);
      } else {
        response = await apiService.post('/training/tracks', payload);
      }

      if (response.success) {
        setSuccess(publish ? 'Track published successfully!' : 'Track saved as draft');
        setTimeout(() => navigate('/training'), 1500);
      }
    } catch (error: any) {
      console.error('Error saving track:', error);
      setError(error.error || error.message || 'Failed to save track');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout title={isEditMode ? 'Edit Training Track' : 'Create Training Track'}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate('/training')}>
              <BackIcon />
            </IconButton>
            <Typography variant="h4" component="h1">
              {isEditMode ? 'Edit Training Track' : 'Create Training Track'}
            </Typography>
          </Box>
          <Box>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={() => handleSave(false)}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              Save Draft
            </Button>
            <Button
              variant="contained"
              startIcon={<PublishIcon />}
              onClick={() => handleSave(true)}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Publish Track'}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label="Track Details" data-tutorial="track-details-tab" />
            <Tab label="Training Items" data-tutorial="training-items-tab" />
            <Tab label="User Assignment" data-tutorial="user-assignment-tab" />
          </Tabs>
        </Box>

        {/* Tab 1: Track Details */}
        {activeTab === 0 && (
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Track Name"
                  value={formData.trackName}
                  onChange={(e) => setFormData({ ...formData, trackName: e.target.value })}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Customer Tone</InputLabel>
                  <Select
                    value={formData.customerTone}
                    onChange={(e: any) => setFormData({ ...formData, customerTone: e.target.value })}
                    label="Customer Tone"
                  >
                    <MenuItem value="neutral">Neutral — Calm and professional</MenuItem>
                    <MenuItem value="curious">Curious — Asks lots of follow-ups</MenuItem>
                    <MenuItem value="skeptical">Skeptical — Needs convincing</MenuItem>
                    <MenuItem value="frustrated">Frustrated — Impatient and demanding</MenuItem>
                    <MenuItem value="aggressive">Aggressive — Confrontational and challenging</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    The personality the AI customer will use throughout the conversation
                  </Typography>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Estimated Duration (minutes, optional)"
                  type="number"
                  value={formData.estimatedDuration || ''}
                  onChange={(e) => setFormData({ ...formData, estimatedDuration: parseInt(e.target.value) || 0 })}
                  helperText="Leave empty if unsure — duration depends on how the session goes"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Tags
                </Typography>
                <Box display="flex" gap={1} mb={1}>
                  <TextField
                    size="small"
                    placeholder="Add tag... (Press Enter)"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAddTag();
                      }
                    }}
                  />
                  <Button onClick={handleAddTag} variant="outlined" size="small">
                    Add
                  </Button>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {formData.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleDeleteTag(tag)}
                      size="small"
                    />
                  ))}
                </Stack>
              </Grid>

            </Grid>
          </Paper>
        )}

        {/* Tab 2: Training Items */}
        {activeTab === 1 && (
          <Box>
            {/* AI Customer Simulation Settings */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                AI Customer Simulation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                During this training, an AI will play the role of a customer in a realistic sales conversation.
                It will naturally bring up knowledge base items and playbook topics as part of a flowing dialogue.
                The rep practices responding in real time and is scored on each item.
              </Typography>

              <Grid container spacing={3} data-tutorial="difficulty-pushback-settings">
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Customer Difficulty</InputLabel>
                    <Select
                      value={formData.difficulty}
                      onChange={(e: any) => setFormData({ ...formData, difficulty: e.target.value })}
                      label="Customer Difficulty"
                    >
                      {Object.values(DifficultyLevel).map((level) => (
                        <MenuItem key={level} value={level}>
                          {DIFFICULTY_LABELS[level]}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Scoring strictness — beginner accepts reasonable answers (pass at 60),
                      intermediate requires specifics (70), advanced demands precision (80).
                    </Typography>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Customer Pushback</InputLabel>
                    <Select
                      value={formData.pushbackLevel}
                      onChange={(e: any) => setFormData({ ...formData, pushbackLevel: e.target.value })}
                      label="Customer Pushback"
                    >
                      {Object.values(PushbackLevel).map((level) => (
                        <MenuItem key={level} value={level}>
                          {PUSHBACK_LABELS[level]}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Challenge persistence — max pushback rounds per item: low&nbsp;=&nbsp;1,
                      medium&nbsp;=&nbsp;3, high&nbsp;=&nbsp;5.
                    </Typography>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {/* Inner tabs: Knowledge Base Items / Sales Playbook */}
            <Paper sx={{ p: 0 }}>
              <Tabs
                value={simulationTab}
                onChange={(_e, v) => setSimulationTab(v)}
                sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label={`Knowledge Base Items (${formData.items.length})`} />
                <Tab
                  label={formData.playbookConfig.enabled ? 'Sales Playbook (enabled)' : 'Sales Playbook'}
                />
              </Tabs>

              <Box sx={{ p: 3 }}>
                {/* Sub-tab 0: Knowledge Base Items */}
                {simulationTab === 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Add items from your knowledge base that the AI customer will bring up during the conversation —
                      objections, FAQs, compliance landmines, and competitor mentions.
                    </Typography>

                    <Box data-tutorial="add-items-buttons" sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button size="small" variant="outlined" onClick={() => handleOpenItemDialog('objection')}>
                        + Objections
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => handleOpenItemDialog('faq')}>
                        + FAQs
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => handleOpenItemDialog('landmine')}>
                        + Landmines
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => handleOpenItemDialog('competitor')}>
                        + Competitors
                      </Button>
                    </Box>

                    <Box data-tutorial="items-list-area">
                      {formData.items.length === 0 ? (
                        <Alert severity="info">
                          No items added yet. Use the buttons above to add objections, FAQs, landmines, or competitors
                          that the AI customer should bring up during the conversation.
                        </Alert>
                      ) : (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext
                            items={formData.items.map((item) => item.itemId)}
                            strategy={verticalListSortingStrategy}
                          >
                            {formData.items.map((item) => (
                              <SortableItem
                                key={item.itemId}
                                item={item}
                                onDelete={handleDeleteItem}
                              />
                            ))}
                          </SortableContext>
                        </DndContext>
                      )}
                    </Box>
                  </>
                )}

                {/* Sub-tab 1: Sales Playbook */}
                {simulationTab === 1 && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Train the rep on your sales playbook. When enabled, the AI customer will open the call
                      expecting a pitch, wait for discovery questions, and test whether the rep can close
                      and set next steps — all based on the playbook you select.
                    </Typography>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.playbookConfig.enabled}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              playbookConfig: { ...prev.playbookConfig, enabled: e.target.checked },
                            }))
                          }
                        />
                      }
                      label="Enable playbook training in this session"
                    />

                    {formData.playbookConfig.enabled && (
                      <Box sx={{ mt: 2, p: 2.5, bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 2 }}>
                        <Stack spacing={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Customer Industry</InputLabel>
                            <Select
                              value={formData.playbookConfig.industry}
                              label="Customer Industry"
                              onChange={(e) => {
                                const industry = e.target.value as string;
                                // Auto-select the first role of the chosen
                                // industry (the only role, if there's just one).
                                const roles =
                                  playbookIndustries.find((ind) => ind.industry === industry)?.roles || [];
                                setFormData((prev) => ({
                                  ...prev,
                                  playbookConfig: {
                                    ...prev.playbookConfig,
                                    industry,
                                    roleName: roles[0]?.name || '',
                                  },
                                }));
                              }}
                            >
                              {playbookIndustries.map((ind) => (
                                <MenuItem key={ind.industry} value={ind.industry}>
                                  {ind.displayName || ind.industry}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {formData.playbookConfig.industry && (
                            <FormControl fullWidth size="small">
                              <InputLabel>Customer Role</InputLabel>
                              <Select
                                value={formData.playbookConfig.roleName}
                                label="Customer Role"
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    playbookConfig: { ...prev.playbookConfig, roleName: e.target.value as string },
                                  }))
                                }
                              >
                                {(playbookIndustries.find((ind) => ind.industry === formData.playbookConfig.industry)?.roles || []).map(
                                  (role) => (
                                    <MenuItem key={role.name} value={role.name}>
                                      {role.displayName || role.name}
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                            </FormControl>
                          )}

                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            When enabled, the rep is expected to deliver the
                            whole playbook — the opening pitch, the discovery
                            questions, and the closing / next-step points.
                          </Typography>
                        </Stack>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Paper>
          </Box>
        )}

        {/* Tab 3: User Assignment */}
        {activeTab === 2 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Assign Users to Track
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Select which users should have access to this training track. You are automatically included.
            </Typography>

            <List>
              {users.map((u) => {
                const isCurrentUser = u.firebaseUid === currentUserUid;
                return (
                  <ListItem key={u.firebaseUid || u._id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.assignedUsers.includes(u.firebaseUid)}
                          disabled={isCurrentUser}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData((prev) => ({
                                ...prev,
                                assignedUsers: [...prev.assignedUsers, u.firebaseUid],
                              }));
                            } else {
                              setFormData((prev) => ({
                                ...prev,
                                assignedUsers: prev.assignedUsers.filter((uid) => uid !== u.firebaseUid),
                              }));
                            }
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">
                            {u.name}{isCurrentUser ? ' (you)' : ''}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {u.email}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>

            {users.length === 0 && (
              <Alert severity="info">
                No users available. Please create users first.
              </Alert>
            )}
          </Paper>
        )}

        {/* Item Selection Dialog */}
        <Dialog
          open={itemDialogOpen}
          onClose={() => setItemDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              height: '80vh',
              maxHeight: '80vh',
            }
          }}
        >
          <DialogTitle>Add {itemType.charAt(0).toUpperCase() + itemType.slice(1)}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
            {/* Search box - only for objections and FAQs */}
            {(itemType === 'objection' || itemType === 'faq') && (
              <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={`Search ${itemType}s...`}
                  value={itemSearchTerm}
                  onChange={(e) => setItemSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                    ),
                    endAdornment: itemSearchTerm && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setItemSearchTerm('');
                          setIsItemSearchMode(false);
                          setItemSearchResults([]);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ),
                  }}
                />
                {isItemSearchMode && (
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={`Search Results: ${itemSearchResults.length} found`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Scrollable List Container */}
            <Box sx={{ flex: 1, overflow: 'auto', px: 3 }}>
              {(loadingItems || itemSearchLoading) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <List>
                    {(isItemSearchMode ? itemSearchResults : availableItems).map((item) => {
                      const isChecked = selectedItems.includes(item._id);
                      return (
                      <ListItem key={item._id} disablePadding>
                        <ListItemButton
                          dense
                          onClick={() => {
                            if (selectedItems.includes(item._id)) {
                              setSelectedItems(selectedItems.filter((id) => id !== item._id));
                            } else {
                              setSelectedItems([...selectedItems, item._id]);
                            }
                          }}
                        >
                          <Checkbox
                            checked={isChecked}
                            edge="start"
                          />
                          <ListItemText
                            primary={item.question || item.trigger || item.name}
                            secondary={
                              <Box component="span" sx={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                                {(itemType === 'objection' || itemType === 'faq') && (
                                  <Chip
                                    label="Original"
                                    size="small"
                                    sx={{
                                      height: '20px',
                                      fontSize: '0.7rem',
                                      backgroundColor: '#DBEAFE',
                                      color: '#1E40AF'
                                    }}
                                  />
                                )}
                                {item.category && (
                                  <Chip
                                    label={
                                      (itemType === 'objection' || itemType === 'faq')
                                        ? TOPIC_CATEGORY_LABELS[item.category as TopicCategory] || item.category
                                        : itemType === 'competitor'
                                        ? COMPETITOR_TYPE_LABELS[item.category as keyof typeof COMPETITOR_TYPE_LABELS] || item.category
                                        : item.category
                                    }
                                    size="small"
                                    sx={{
                                      height: '20px',
                                      fontSize: '0.7rem',
                                      backgroundColor: '#F3E8FF',
                                      color: '#6B21A8'
                                    }}
                                  />
                                )}
                              </Box>
                            }
                            slotProps={{
                              secondary: {
                                component: 'div'
                              }
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                      );
                    })}
                  </List>
                  {(isItemSearchMode ? itemSearchResults : availableItems).length === 0 && (
                    <Alert severity="info">
                      {isItemSearchMode
                        ? `No results found for "${itemSearchTerm}". Try a different search term.`
                        : `No ${itemType}s available. Please create some first.`}
                    </Alert>
                  )}
                </>
              )}
            </Box>

            {/* Fixed Pagination at Bottom */}
            {totalItemPages > 1 && (
              <Box sx={{ borderTop: 1, borderColor: 'divider', py: 2, px: 3, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  count={totalItemPages}
                  page={currentItemPage}
                  onChange={(_, page) => handleItemPageChange(page)}
                  color="primary"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setItemDialogOpen(false)} disabled={addingItems}>
              Cancel
            </Button>
            <Button
              onClick={handleAddItems}
              variant="contained"
              disabled={selectedItems.length === 0 || addingItems}
              startIcon={addingItems ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {addingItems ? 'Adding...' : `Add Selected (${selectedItems.length})`}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </MainLayout>
  );
};

export default TrainingTrackBuilder;
